import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export function useUnreadCount(ticketId: string | null) {
  const [unreadCount, setUnreadCount] = useState(0);

  const updateUnreadCount = useCallback(async () => {
    if (!ticketId) {
      setUnreadCount(0);
      return;
    }

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { count, error } = await supabase
        .from('support_messages')
        .select('*', { count: 'exact', head: true })
        .eq('ticket_id', ticketId)
        .eq('is_from_admin', true)
        .is('read_at', null);

      if (error) throw error;
      setUnreadCount(count || 0);
    } catch (error: any) {
      console.error('Error fetching unread count:', error?.message || error);
      setUnreadCount(0);
    }
  }, [ticketId]);

  useEffect(() => {
    updateUnreadCount();
  }, [updateUnreadCount]);

  return {
    unreadCount,
    updateUnreadCount,
  };
}

export function useTotalUnreadCount() {
  const [totalUnread, setTotalUnread] = useState(0);

  useEffect(() => {
    const fetchTotalUnread = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) return;

        const { data: tickets, error: ticketsError } = await supabase
          .from('support_tickets')
          .select('id')
          .eq('user_id', user.id)
          .in('status', ['open', 'in_progress']);

        if (ticketsError) {
          // Se a tabela não existe, coluna ausente ou há erro de permissão, retornar 0 silenciosamente
          const msg = ticketsError.message ?? '';
          const isRecoverable =
            ticketsError.code === 'PGRST116' ||
            msg.includes('schema cache') ||
            msg.includes('permission denied') ||
            msg.includes('Could not find') ||
            msg.includes('does not exist') ||
            ticketsError.code === '42P01' ||
            ticketsError.code === '42501';
          if (isRecoverable) {
            console.warn('Tabela support_tickets não encontrada ou sem permissão, retornando 0');
            setTotalUnread(0);
            return;
          }
          throw ticketsError;
        }
        if (!tickets || tickets.length === 0) {
          setTotalUnread(0);
          return;
        }

        const ticketIds = tickets.map((t) => t.id);

        const { count, error } = await supabase
          .from('support_messages')
          .select('*', { count: 'exact', head: true })
          .in('ticket_id', ticketIds)
          .eq('is_from_admin', true)
          .is('read_at', null);

        if (error) throw error;
        setTotalUnread(count || 0);
      } catch (error: any) {
        // Tratar erros de permissão (403/400) ou tabela não encontrada silenciosamente
        const msg = error?.message ?? '';
        const isRecoverable =
          error?.code === 'PGRST116' ||
          msg.includes('schema cache') ||
          msg.includes('permission denied') ||
          msg.includes('Could not find') ||
          msg.includes('does not exist') ||
          error?.code === '42P01' ||
          error?.code === '42501';
        if (isRecoverable) {
          console.warn('Erro de permissão ou tabela não encontrada, retornando 0:', msg || error?.code);
          setTotalUnread(0);
          return;
        }
        console.error('Error fetching total unread count:', msg || error);
        setTotalUnread(0);
      }
    };

    fetchTotalUnread();
    const interval = setInterval(fetchTotalUnread, 30000);

    const channel = supabase
      .channel('unread_count_updates')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'support_messages', filter: 'is_from_admin=eq.true' }, () => fetchTotalUnread())
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'support_messages' }, () => fetchTotalUnread())
      .subscribe();

    return () => {
      clearInterval(interval);
      supabase.removeChannel(channel);
    };
  }, []);

  return totalUnread;
}
